import { useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import toothesSvgRaw from './toothes.svg?raw'

interface ToothChartProps {
  highlighted: number[]
  toothServices?: Record<number, string[]>
  primaryColor?: string
  className?: string
  legend?: boolean
}

const SVG_INNER = (() => {
  const opening = toothesSvgRaw.indexOf('>')
  const closing = toothesSvgRaw.lastIndexOf('</svg>')
  return opening >= 0 && closing > opening
    ? toothesSvgRaw.slice(opening + 1, closing).trim()
    : ''
})()

export function ToothChart({
  highlighted,
  toothServices = {},
  primaryColor = 'rgba(198, 172, 74, 1)',
  className,
  legend = true,
}: ToothChartProps) {
  const set = useMemo(() => new Set(highlighted.map((n) => Number(n)).filter((n) => Number.isFinite(n))), [highlighted])
  const [popover, setPopover] = useState<{
    tooth: number
    services: string[]
    x: number
    y: number
  } | null>(null)

  const css = useMemo(() => {
    const base = `
      .lk-tooth-chart svg path[data-value] { fill: #ffffff; stroke: #49474C; stroke-width: 0.9; transition: fill .2s ease, stroke .2s ease, filter .2s ease; cursor: default; }
      .lk-tooth-chart svg path:not([data-value]) { fill: #5f5b6a; }
      .lk-tooth-chart svg path[data-value].lk-tooth-interactive { cursor: pointer; }
      .lk-tooth-chart svg path[data-value].lk-tooth-interactive:hover { stroke-width: 2.4 !important; filter: drop-shadow(0 0 3px __ACCENT__); }
      .lk-tooth-chart .lk-tooth-active { fill: #ffffff !important; stroke: __ACCENT__ !important; stroke-width: 2.4 !important; filter: drop-shadow(0 0 3px __ACCENT__); }
    `.replace(/__ACCENT__/g, primaryColor)
    if (set.size === 0) return base
    const rules = Array.from(set)
      .map((n) => `.lk-tooth-chart svg path[data-value="${n}"]`)
      .join(',\n')
    return `${base}\n${rules} { fill: #ffffff; stroke: ${primaryColor}; stroke-width: 2.4; filter: drop-shadow(0 0 3px ${primaryColor}); cursor: pointer; }`
  }, [set, primaryColor])

  if (!SVG_INNER) return null

  function getToothFromEvent(event: MouseEvent<HTMLDivElement>): number | null {
    const target = event.target instanceof Element
      ? event.target.closest('path[data-value]')
      : null
    const raw = target?.getAttribute('data-value')
    if (!raw) return null
    const tooth = Number(raw)
    return Number.isFinite(tooth) ? tooth : null
  }

  function showPopover(event: MouseEvent<HTMLDivElement>) {
    const tooth = getToothFromEvent(event)
    if (tooth === null || !set.has(tooth)) return
    const services = toothServices[tooth] ?? []
    if (services.length === 0) return
    const rect = event.currentTarget.getBoundingClientRect()
    setPopover({
      tooth,
      services,
      x: Math.min(Math.max(event.clientX - rect.left, 96), Math.max(rect.width - 96, 96)),
      y: Math.min(Math.max(event.clientY - rect.top, 72), Math.max(rect.height - 72, 72)),
    })
  }

  return (
    <div
      className={`lk-tooth-chart relative ${className ?? ''}`}
      onMouseMove={showPopover}
      onMouseLeave={() => setPopover(null)}
      onClick={(event) => {
        const tooth = getToothFromEvent(event)
        if (tooth === null || !set.has(tooth)) {
          setPopover(null)
          return
        }
        if (popover?.tooth === tooth) {
          setPopover(null)
          return
        }
        showPopover(event)
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <svg
        viewBox="0 0 378 498"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        role="img"
        aria-label="Зубная формула"
        className="mx-auto block w-full max-w-[380px] h-auto"
        dangerouslySetInnerHTML={{ __html: SVG_INNER }}
      />
      {popover ? (
        <div
          className="pointer-events-none absolute z-20 w-[min(18rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-full rounded-2xl border border-gray-200 bg-white/95 p-3 text-left shadow-xl backdrop-blur"
          style={{ left: popover.x, top: popover.y - 12 }}
        >
          <div className="mb-1 flex items-center gap-2">
            <span
              className="inline-flex h-7 min-w-7 items-center justify-center rounded-full border bg-white px-2 text-xs font-bold"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              {popover.tooth}
            </span>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Услуги по зубу</p>
          </div>
          <div className="max-h-40 overflow-y-auto pr-1">
            {popover.services.map((service, index) => (
              <p key={`${service}-${index}`} className="border-t border-gray-100 py-1.5 text-xs leading-snug text-gray-800 first:border-t-0">
                {service}
              </p>
            ))}
          </div>
        </div>
      ) : null}
      {legend && set.size > 0 ? (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
          {Array.from(set)
            .sort((a, b) => a - b)
            .map((n) => (
              <span
                key={n}
                className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-md border bg-white px-2 text-xs font-semibold shadow-sm"
                style={{ borderColor: primaryColor, color: primaryColor }}
              >
                {n}
              </span>
            ))}
        </div>
      ) : null}
    </div>
  )
}
